import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { captureError } from "../utils/errorHandler";
import { getOnboardingStepRoute } from "../utils/onboardingRoutes";

const DashboardRedirect = ({
  children,
  requireComplete = true,
  currentStep = null,
}) => {
  const [hasChecked, setHasChecked] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Get onboarding status from Redux (pre-fetched by App.jsx)
  const { onboardingCompleted, onboardingStep, hasFetched } = useSelector(
    (state) => state.dashboard
  );

  // Handle redirects based on onboarding status
  useEffect(() => {
    // Wait until we have fetched data
    if (!hasFetched) return;
    
    // Prevent multiple checks
    if (hasChecked) return;

    try {
        // If accessing dashboard, check if onboarding is complete
        if (requireComplete) {
        if (onboardingCompleted === false) {
            // Redirect to appropriate onboarding step (checkTaxReturnRun=true for login redirects)
          const route = getOnboardingStepRoute(onboardingStep, true);
            navigate(route, { replace: true });
        }
      }
      // If accessing onboarding page, check if they're allowed
        else if (currentStep !== null) {
        if (onboardingCompleted === true) {
            // Onboarding complete, go to the tax app's confirm surface.
            navigate("/onboarding/confirm", { replace: true });
        } else if (onboardingCompleted === false) {
          // Only redirect if user is trying to access a step they haven't unlocked yet
          // Allow going backwards to previous steps (don't redirect)
          const discoveryCanProceed = localStorage.getItem("discoveryCanProceed") === "true";
          const hasTaxReturnRun = localStorage.getItem("hasTaxReturnRun") === "true";
          
          // Check what pages the user has unlocked:
          // - Step 1 (upload FY 2024): Always unlocked
          // - Step 1b (upload FY 2025): Always unlocked (user can navigate freely between upload pages)
          // - Step 2 (questionnaire): Unlocked if onboardingStep >= 2
          // - Confirm: Unlocked if discoveryCanProceed is true OR hasTaxReturnRun is true
          
          const isQuestionnairePage = location.pathname === "/onboarding/questionnaire";
          const isConfirmPage = location.pathname === "/onboarding/confirm";
          
          // Only redirect if trying to access a page they shouldn't have access to
          if (isQuestionnairePage && onboardingStep < 2) {
            // Can't access questionnaire yet - redirect to upload
            navigate("/onboarding/upload", { replace: true });
          } else if (isConfirmPage && !discoveryCanProceed && !hasTaxReturnRun) {
            // Can't access confirm yet (no discoveryCanProceed and no tax_return_run) - redirect to appropriate step
            // Don't check tax_return_run here since we already checked it above
            const route = getOnboardingStepRoute(onboardingStep, false);
            navigate(route, { replace: true });
            }
          // Otherwise, allow the user to stay on the page (including going backwards)
          // Important: We DON'T redirect from questionnaire to confirm even if hasTaxReturnRun is true
          // This allows users to manually navigate back to questionnaire via back button or URL
        }
        }
      } catch (error) {
        captureError(error, "checking dashboard/onboarding status");
        // On error for dashboard, redirect to onboarding
        if (requireComplete) {
          navigate("/onboarding/upload", { replace: true });
      }
    }

    setHasChecked(true);
  }, [
    hasFetched,
    hasChecked,
    onboardingCompleted,
    onboardingStep,
    navigate,
    location.pathname,
    requireComplete,
    currentStep,
  ]);

  // Always render children immediately - redirects happen in the background
  return children;
};

export default DashboardRedirect;
